/* eslint-disable max-lines-per-function, max-statements */
import { useQuery } from "@apollo/client";
import { Link, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import css from "classnames";
import { identity } from "lodash";
import React from "react";
import { Box, Grid } from "@mui/material";
import { graphqlErrorIndicatesForbidden } from "utils/graphql-auth-error";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { Viewer } from "components/auth/types";
import { ServerError } from "components/graphql-views";
import {
  Feature,
  QueryInterestProfile,
  QueryInterestProfileVariables,
  UserRole,
} from "types/gql-types";
import { isPropertyFeatureEnabled } from "pages/dashboard/interests";
import { useSidebarFilters } from "pages/dashboard/interests/sidebar-filters";
import { deriveCanAssignOwner } from "pages/dashboard/interests/interests-table/can-assign-owner";
import { toOwnerSummaries } from "components/common/owners-editor/use-owner-changes";
import Activity from "../../interests-activities/activity-page";
import Documents from "../../interests-documents/components/index";
import InterestUpdatePage from "../../interests-update/interest-update-page";
import { QUERY_INTEREST_PROFILE } from "../query";
import Actions from "./actions";
import Details from "./details";
import Download from "./download";
import styles from "./index.module.css";
import Navigation from "./navigation";
import OwnerControlContainer from "./owner-control-container";

type Props = { viewer: Viewer }

const ApplicantProfileViewComponent: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pageState } = useSidebarFilters();
  const query = useQuery<QueryInterestProfile, QueryInterestProfileVariables>(
    QUERY_INTEREST_PROFILE,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        interestId: (window.location.pathname.split("/").slice(-2)[0]) || "",
      },
    },
  );

  // interestID comes from the route: /dash/manager/interest/:interestID/*
  const interestIDFromPath = (window.location.pathname.split("/").slice(-2)[0]) || "";
  if (!interestIDFromPath) return null;

  if (query.loading) {
    return null;
  }

  if (query.error && graphqlErrorIndicatesForbidden(query.error)) {
    return <Navigate to="/not-authorized" replace />;
  }

  if (
    query.error ||
    !query.data?.interest?.interestList.property
  ) {
    return <ServerError desc={"There was a problem loading the page."} />;
  }

  const {
    interest: {
      id,
      householdMember0FirstName: firstName,
      householdMember0LastName: lastName,
      status: { name: status },
      owners,
      interestList: { id: listID, name: listName, defaultSorting, property: { features } },
    },
  } = query.data;

  // Hides add/remove affordances for restricted PMs (owners still display).
  const canAssignOwner = deriveCanAssignOwner(props.viewer);

  const documentsFeatureEnabled = isPropertyFeatureEnabled(features, Feature.DOCUMENT_UPLOAD);

  const slideoutEnabled = isPropertyFeatureEnabled(features, Feature.INTEREST_PROFILE_SLIDEOUT);
  if (slideoutEnabled) {
    return <Navigate to={`/dash/manager/lists/${listID}/p/1/interests/${id}/activity`} replace />;
  }

  const links = [
    { name: "Messages & activity", to: "activity", icon: "fa-clock-o" },
    { name: "Application details", to: "update", icon: "fa-pencil" },
    {
      name: "Documents",
      to: "documents",
      icon: "fa-file",
      disabled: !documentsFeatureEnabled,
    },
  ];

  // Determine active tab without using hooks inside a loop
  const { pathname } = location;
  const isActivityPath = pathname.endsWith("/activity") || (!pathname.endsWith("/update") && !pathname.endsWith("/documents"));
  const activeByTo: Record<string, boolean> = {
    activity: isActivityPath,
    update: pathname.endsWith("/update"),
    documents: pathname.endsWith("/documents"),
  };

  return (
    <div className={css("container", styles.profile)}>
      <div className={css("card", styles.header)}>
        <div className={styles.labels}>
          <Link className={styles.label}
            to={`/dash/manager/lists/${listID}/p/${(pageState.page ?? 0) + 1}`}>
            {listName}
          </Link>
          <span className={css(styles.divider, "fa", "fa-chevron-right")} />
          <span className={styles.label}>{[firstName, lastName].filter(identity).join(" ")}</span>
        </div>
        <div className={styles.controls}>
          <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
            <OwnerControlContainer
              interestId={id}
              owners={toOwnerSummaries(owners)}
              currentUserId={props.viewer.id}
              currentUserName={props.viewer.fullName ?? ""}
              canAssign={canAssignOwner}
              applicantName={[firstName, lastName].filter(Boolean).join(" ")}
            />
          </Box>
          <div className={styles.download}>
            <Download interestID={id} />
          </div>
          <div className={styles.actions}>
            <Actions currentListId={listID} currentInterestId={id} currentStatus={status} />
          </div>
        </div>
      </div>
      <br/>
      <Grid container spacing={2}>
        <Grid size={{ sm: 3, xs: 12 }}>
          <Details query={query} />
        </Grid>
        <Grid size={{ sm: 9, xs: 12 }}>
          <div className={styles.navigation}>
            <ul className={css("nav", "nav-tabs", styles.sections)}>
              {links
                .filter((link) => !link.disabled)
                .map((link) => {
                  const isActive = activeByTo[link.to] ?? pathname.endsWith(`/${link.to}`);
                  const preserveSearch = location.search && location.search.includes("offset=") ? location.search : "";
                  return (
                    <li
                      key={link.name}
                      role="presentation"
                      className={css({ active: isActive })}
                    >
                      <Link
                        to={{
                          pathname: `/dash/manager/interest/${id}/${link.to}`,
                          search: preserveSearch,
                        }}
                      >
                        <span
                          className={`fa ${link.icon}`}
                          style={{ paddingRight: 8 }}
                        />
                        {link.name}
                      </Link>
                    </li>
                  );
                })}
            </ul>
            <div>
              <Navigation interestID={id} listID={listID} defaultSorting={defaultSorting || "POSITION_NUMBER"} />
            </div>
          </div>
          <Routes>
            <Route index element={
              <Activity
                viewer={props.viewer}
                interestID={id}
                interestListID={listID}
              />
            } />
            <Route path="activity" element={<Activity viewer={props.viewer} interestID={id} interestListID={listID} />} />
            <Route path="update" element={
              <InterestUpdatePage
                interestID={id}
                interestListID={listID}
                onCancel={() => navigate("..")}
                onSubmit={() => new Promise((resolve) => {
                  query.refetch().then(() => resolve());
                })}
              />
            } />
            <Route path="documents" element={<Documents path="" interestID={id} enabled={documentsFeatureEnabled} />} />
          </Routes>
        </Grid>
      </Grid>
    </div>
  );
};

export const ApplicantProfileView = authorizeForRole<Record<string, unknown>>([
  UserRole.PROPERTY_MANAGER,
  UserRole.SUPERVISOR,
], ApplicantProfileViewComponent as unknown as React.FC<Record<string, unknown>>);
